/**
 * Path + query scrubbing for analytics.
 *
 * cinch_ops routes carry record identifiers — account UUIDs, contract ids,
 * close periods — and its query strings carry filter values that can name a
 * customer outright (`?account=Tire%20Discounters`). None of that may leave
 * the browser. PostHog gets the *shape* of the route, never the row it
 * pointed at.
 *
 * Two rules:
 *   - Path segments that look like an identifier collapse to `:id`; a
 *     year-month(-day) segment collapses to `:period` (the close-period routes
 *     under /reconciliation and /cos-units). The route shape survives, so
 *     "how often does anyone open a reconciliation step" is still answerable.
 *   - Query values are replaced with `:redacted` unless the key is on
 *     SAFE_PARAMS — an allowlist of low-cardinality UI state (which tab, which
 *     range) that's useful in analysis and cannot name anything.
 *
 * The identifier patterns are deliberately conservative about kebab-case:
 * every route segment we own is kebab-case with no digits, and every id we've
 * seen is either a UUID, all digits, or a long digit-bearing token. A future
 * route named `some-very-long-feature-name` must not silently become `:id`.
 */

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const NUMERIC = /^\d+$/;
const HEX_TOKEN = /^[0-9a-f]{16,}$/i;
/** Opaque token: 16+ chars, contains a digit, no hyphens (so kebab-case routes are safe). */
const OPAQUE_TOKEN = /^(?=.*\d)[A-Za-z0-9_]{16,}$/;
/** Close/report period: 2026-08 or 2026-08-11. */
const PERIOD = /^\d{4}-\d{2}(?:-\d{2})?$/;

/** Query keys whose values are UI state, never data. Values still length-capped. */
const SAFE_PARAMS = new Set([
  "tab",
  "view",
  "mode",
  "period",
  "range",
  "status",
  "sort",
  "dir",
  "group",
  "scenario",
  "cadence",
  "page",
]);

const MAX_SAFE_VALUE = 64;
const REDACTED = ":redacted";

function sanitizeSegment(seg: string): string {
  if (!seg) return seg;
  if (PERIOD.test(seg)) return ":period";
  if (UUID.test(seg) || NUMERIC.test(seg) || HEX_TOKEN.test(seg) || OPAQUE_TOKEN.test(seg)) {
    return ":id";
  }
  return seg;
}

/** `/contracts/9f1c.../timeline` → `/contracts/:id/timeline`. */
export function sanitizePathname(pathname: string): string {
  if (!pathname) return "/";
  const joined = pathname.split("/").map(sanitizeSegment).join("/");
  return joined || "/";
}

/** `?account=Acme&tab=summary` → `?account=:redacted&tab=summary`. */
export function sanitizeSearch(search: string): string {
  const raw = search.startsWith("?") ? search.slice(1) : search;
  if (!raw) return "";
  const parts: string[] = [];
  for (const [key, value] of new URLSearchParams(raw)) {
    const keep = SAFE_PARAMS.has(key.toLowerCase()) && value.length <= MAX_SAFE_VALUE;
    // Build by hand rather than URLSearchParams.toString(): that would encode
    // the `:` in the sentinel to %3A and make every redacted value unreadable
    // in the PostHog UI.
    parts.push(`${encodeURIComponent(key)}=${keep ? encodeURIComponent(value) : REDACTED}`);
  }
  return parts.length ? `?${parts.join("&")}` : "";
}

/**
 * Scrub a full URL or a relative one. The hash is dropped entirely — TanStack
 * puts nothing there, so anything present is not ours to forward.
 */
export function sanitizeUrl(url: string): string {
  if (!url) return "";
  try {
    const absolute = /^https?:\/\//i.test(url);
    const parsed = new URL(url, "http://localhost");
    const path = sanitizePathname(parsed.pathname) + sanitizeSearch(parsed.search);
    if (!absolute) return path;
    return `${parsed.origin}${path}`;
  } catch {
    // Unparseable — forward nothing rather than guess.
    return "";
  }
}

/** Every posthog-js property that can carry a URL. Scrubbed on every event. */
const URL_PROPERTIES = [
  "$current_url",
  "$referrer",
  "$initial_current_url",
  "$initial_referrer",
  "$initial_pathname",
] as const;

/**
 * posthog-js `sanitize_properties` hook — the last gate before an event leaves
 * the browser. Runs on every event including the built-in ones we don't emit
 * ourselves, which is exactly why it exists: `$pageview` attaches
 * `window.location.href` on its own and would otherwise ship raw ids.
 */
export function sanitizeProperties(
  properties: Record<string, unknown>,
): Record<string, unknown> {
  const out = { ...properties };
  for (const key of URL_PROPERTIES) {
    const value = out[key];
    if (typeof value === "string") out[key] = sanitizeUrl(value);
  }
  if (typeof out.$pathname === "string") out.$pathname = sanitizePathname(out.$pathname);
  return out;
}
